import LocationCompactCard from './LocationCompactCard';
import LocationExpandedCard from './LocationExpandedCard';
import { observer } from 'mobx-react';
import { HoverCard, IExpandingCardStyles } from 'office-ui-fabric-react/lib/HoverCard';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { initializeLocationCardIfNeeded } from 'owa-location-card-v2/lib/actions/locationCardActions';
import { getLocationTextStyle } from 'owa-location-persona';
import { PlaceType } from 'owa-location-service';
import PersonaPostalAddress from 'owa-service/lib/contract/PersonaPostalAddress';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { trace } from 'owa-trace';
import * as React from 'react';
import { AriaRoles } from 'owa-accessibility';

let styles = require('./LocationCard.scss');
let classNames = require('classnames/bind').bind(styles);

export interface LocationCardProps {
    name: string;
    postalAddress: PersonaPostalAddress;
    locationId: string;
    locationType?: PlaceType;
    children?: JSX.Element | JSX.Element[];
    colorScheme: ColorScheme;
    locationAriaLabel?: string;
    phoneNumber?: string;
    onLocationCardUnmounted: (...args: any[]) => void;
    hoverTargetStyle?: string;
}

interface ILocationCardState {
    contentRendered?: HTMLDivElement;
    openedByHotkey?: boolean;
}

@observer
export class LocationCardWrapperV2 extends React.Component<LocationCardProps, ILocationCardState> {
    constructor(props: LocationCardProps) {
        super(props);

        this.state = {
            contentRendered: undefined,
        };
    }

    componentDidMount() {
        // Server side bug where some ids from SDS are returned without a location Id. This server bugs
        // should not block the client
        if (isNullOrWhiteSpace(this.props.locationId)) {
            trace.warn('Location card expects a locationId to be set');
        } else {
            // prefetch the location from server
            initializeLocationCardIfNeeded(
                this.props.name,
                this.props.postalAddress,
                this.props.locationId,
                this.props.locationType,
                this.props.colorScheme,
                this.props.phoneNumber
            );
        }
    }

    render(): JSX.Element {
        if (isNullOrWhiteSpace(this.props.locationId)) {
            trace.warn('Location card expects a locationId to be set');
            return <> {this.props.children} </>;
        }

        let customStyle: IExpandingCardStyles = {
            expandedCard: [
                {
                    selectors: {
                        ':before': {
                            display: 'none',
                        },
                    },
                },
            ],

            expandedCardScroll: ['customScrollBar'],
        };
        return (
            // For accessibility I'm following the example from fabric that uses state.contentRendered
            // https://developer.microsoft.com/en-us/fabric#/components/hovercard#Implementation Checkout Example 2: Expanding HoverCard using Target, DirectionalHint and custom HotKey
            <span
                ref={(c: HTMLDivElement) =>
                    !this.state.contentRendered && this.setState({ contentRendered: c })
                }
                data-is-focusable={true}
                tabIndex={0}
                aria-label={this.props.locationAriaLabel}
                role={AriaRoles.button}
                className={classNames(
                    this.props.hoverTargetStyle ? this.props.hoverTargetStyle : styles.hoverTarget,
                    getLocationTextStyle(this.props.colorScheme)
                )}
                onKeyDown={this.onKeyDown}>
                {this.props.children}
                {this.state.contentRendered && (
                    <HoverCard
                        cardDismissDelay={300}
                        cardOpenDelay={300}
                        expandedCardOpenDelay={500}
                        trapFocus={true}
                        setInitialFocus={true}
                        expandingCardProps={{
                            renderData: this.props,
                            onRenderCompactCard: this.onRenderCompactCard,
                            onRenderExpandedCard: this.onRenderExpandedCard,
                            compactCardHeight: 147,
                            expandedCardHeight: 411,
                            className: styles.container,
                            styles: customStyle,
                        }}
                        instantOpenOnClick={true}
                        target={this.state.contentRendered}
                        openHotKey={KeyCodes.space}
                    />
                )}
            </span>
        );
    }

    private onRenderCompactCard(props: LocationCardProps): JSX.Element {
        return (
            <LocationCompactCard
                name={props.name}
                postalAddress={props.postalAddress}
                locationId={props.locationId}
                locationType={props.locationType}
                colorScheme={props.colorScheme}
                onLocationCardUnmounted={props.onLocationCardUnmounted}
            />
        );
    }

    private onRenderExpandedCard(props: LocationCardProps): JSX.Element {
        return (
            <LocationExpandedCard
                name={props.name}
                postalAddress={props.postalAddress}
                locationId={props.locationId}
                locationType={props.locationType}
                colorScheme={props.colorScheme}
                onLocationCardUnmounted={props.onLocationCardUnmounted}
            />
        );
    }

    private onKeyDown(ev: React.KeyboardEvent<unknown>) {
        switch (ev.keyCode) {
            // the hotKey to open card, stop the focus competition between hover target and input control
            case KeyCodes.space:
            // stop the card to be opened by "Enter"
            case KeyCodes.enter:
                ev.stopPropagation();
                return;
        }
    }
}
